import { useState, useRef } from "react";
import { STAGES, PIPELINES, PRIORITY_LEVELS } from "./constants";
import { S, Ic, useClickOutside, focusIn, focusOut } from "./ui";

const QUICK_FILTERS = [
  { code: "today", label: "Today's Activities", fn: o => o.activities === "Today's Activities" },
  { code: "none",  label: "No Activities",      fn: o => !o.activities },
  { code: "over",  label: "Overdue",            fn: () => false },
];

export default function FilterBar({ filters, setFilters, sortBy, setSortBy, opps, teamMembers = [], quickFilter, setQuickFilter }) {
  const [addOpen, setAddOpen] = useState(false);
  const addRef = useRef();
  useClickOutside(addRef, () => setAddOpen(false));

  const hasFilters = Object.values(filters).some(v => v);

  const Sel = ({ value, onChange, label, children }) => (
    <select value={value} onChange={onChange} onFocus={focusIn} onBlur={focusOut}
      style={{ height: "30px", padding: "0 26px 0 9px", border: "1.5px solid #e0e0e8", borderRadius: "8px", fontSize: "12.5px", color: "#3a3a5c", background: "#fafafa url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='8' height='5'%3E%3Cpath d='M1 1l3 3 3-3' stroke='%239e9eb8' stroke-width='1.2' stroke-linecap='round' fill='none'/%3E%3C/svg%3E\") no-repeat right 8px center", appearance: "none", outline: "none", cursor: "pointer", minWidth: "110px" }}>
      <option value="">{label}</option>
      {children}
    </select>
  );

  return (
    <div style={{ background: "#fff", borderBottom: "1px solid #eaeaed", padding: "7px 20px", display: "flex", alignItems: "center", gap: "7px", flexWrap: "wrap", minHeight: "46px" }}>

      {/* Search */}
      <div style={{ position: "relative" }}>
        <span style={{ position: "absolute", left: "9px", top: "50%", transform: "translateY(-50%)", pointerEvents: "none", color: "#9e9eb8" }}><Ic n="search" s={13} /></span>
        <input value={filters.search} onChange={e => setFilters(f => ({ ...f, search: e.target.value }))}
          placeholder="Search opportunities..." onFocus={focusIn} onBlur={focusOut}
          style={{ height: "30px", paddingLeft: "28px", paddingRight: "10px", border: "1.5px solid #e0e0e8", borderRadius: "8px", fontSize: "12.5px", color: "#1a1a2e", outline: "none", background: "#fafafa", width: "200px", transition: "border-color .15s" }} />
      </div>

      {/* Pipeline (replaces Source in filter) */}
      <Sel value={filters.pipeline || ""} onChange={e => setFilters(f => ({ ...f, pipeline: e.target.value }))} label="Pipeline">
        {PIPELINES.map(p => <option key={p}>{p}</option>)}
      </Sel>

      {/* Stage */}
      <Sel value={filters.stage} onChange={e => setFilters(f => ({ ...f, stage: e.target.value }))} label="Stage">
        {STAGES.map(s => <option key={s}>{s}</option>)}
      </Sel>

      {/* Owner — real team members from Supabase */}
      <Sel value={filters.assignee} onChange={e => setFilters(f => ({ ...f, assignee: e.target.value }))} label="Owner">
        {teamMembers.length > 0
          ? teamMembers.map(m => <option key={m.id} value={m.name}>{m.name}</option>)
          : [...new Set(opps.map(o => o.assignee?.name).filter(Boolean))].map(n => <option key={n}>{n}</option>)
        }
      </Sel>

      {/* Priority */}
      <Sel value={filters.priority} onChange={e => setFilters(f => ({ ...f, priority: e.target.value }))} label="Priority">
        {PRIORITY_LEVELS.map(p => <option key={p}>{p}</option>)}
      </Sel>

      {/* Clear */}
      {hasFilters && (
        <button onClick={() => setFilters({ search: "", pipeline: "", stage: "", assignee: "", priority: "" })}
          style={{ height: "28px", padding: "0 10px", border: "1.5px solid #e0e0e8", borderRadius: "7px", background: "#fff", cursor: "pointer", fontSize: "11.5px", color: "#6e6e80" }}>
          ✕ Clear
        </button>
      )}

      <div style={{ flex: 1 }} />

      {/* Quick Filters */}
      <div style={S.row(5)}>
        {QUICK_FILTERS.map(q => {
          const count  = opps.filter(q.fn).length;
          const active = quickFilter === q.code;
          return (
            <button key={q.code} onClick={() => setQuickFilter(qf => qf === q.code ? "" : q.code)}
              style={{ ...S.row(5), height: "26px", padding: "0 9px", borderRadius: "20px", border: `1.5px solid ${active ? "#0f3460" : "#e0e0e8"}`, background: active ? "#e8f0fd" : "transparent", cursor: "pointer", fontSize: "11.5px", fontWeight: active ? "600" : "400", color: active ? "#0f3460" : "#6e6e80", whiteSpace: "nowrap" }}>
              <span style={{ background: active ? "#0f3460" : "#c0c0d8", color: "#fff", borderRadius: "8px", padding: "0 5px", fontSize: "10px", fontWeight: "700" }}>{count}</span>
              {q.label}
            </button>
          );
        })}
      </div>

      <div style={{ width: "1px", height: "20px", background: "#eaeaed" }} />

      {/* Sort */}
      <div style={S.row(6)}>
        <span style={{ fontSize: "11.5px", color: "#9e9eb8" }}>Sort by</span>
        <select value={sortBy} onChange={e => setSortBy(e.target.value)}
          style={{ height: "30px", padding: "0 26px 0 9px", border: "1.5px solid #e0e0e8", borderRadius: "8px", fontSize: "12.5px", color: "#3a3a5c", background: "#fafafa url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='8' height='5'%3E%3Cpath d='M1 1l3 3 3-3' stroke='%239e9eb8' stroke-width='1.2' stroke-linecap='round' fill='none'/%3E%3C/svg%3E\") no-repeat right 8px center", appearance: "none", outline: "none" }}>
          <option value="name">Name</option>
          <option value="amount">Budget</option>
          <option value="stage">Stage</option>
          <option value="endDate">Closing Date</option>
          <option value="probability">Probability</option>
          <option value="created">Created</option>
        </select>
      </div>

    </div>
  );
}
